import React from 'react';
import styles from './style.module.scss';
import NextImage from 'next/image';
import Button, { SECONDARY_THEME } from 'components/control/button';
import useSizeClass from 'hooks/use-size-class';
import useRecaptcha from 'hooks/use-recaptcha';
import { fetchAgentDetailsByNumericKey } from 'utils/agent-endpoint';
import CopyButton from 'components/control/copy-button';
import { agentAssociations, militaryBadgeMapping } from 'constants/military-badge-mappings';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { useQuery } from '@tanstack/react-query';

//#region Constants
const DEFAULT_AGENT_PIC = '/next/assets/images/agent-search/agent-default-profile-pic.png';
const DEFAULT_MILITARY_BADGE_WIDTH = 17;
const DEFAULT_MILITARY_BADGE_HEIGHT = 17;
const DEFAULT_AGENT_AVATAR_WIDTH = 60;
const DEFAULT_AGENT_AVATAR_HEIGHT = 60;
//#endregion

//#region Types
type Milnet = {
  milnetAssociation?: string;
  status?: 'Active Duty' | 'Reserve' | 'Veteran' | 'Retired' | 'Spouse'; // https://github.com/exprealty-com/agent-admin/blob/staging/app/models/milnet_application.rb#L30
};

export type ListingAgentInfo = {
  id: string;
  firstName: string;
  lastName: string;
  cityState?: string;
  email?: string;
  photo?: string;
  phoneNumber?: string;
  milnet?: Milnet;
  title?: string;
};

type ListingAgentProps = {
  expAgentId: number;
  showMilitaryDetails?: boolean;
  onContactAgentClick?: (agentInfo: ListingAgentInfo) => void;
};

type ListingAgentMobileProps = {
  agentInfo: ListingAgentInfo;
  showMilitaryDetails?: boolean;
  onContactAgentClick?: (agentInfo: ListingAgentInfo) => void;
  avatarWidth?: number | `${number}` | undefined;
  avatarHeight?: number | `${number}` | undefined;
};
//#endregion

//#region Sub Components
function MilitaryBadge({ milnetAssociation, width = DEFAULT_MILITARY_BADGE_WIDTH, height = DEFAULT_MILITARY_BADGE_HEIGHT }: { milnetAssociation: string; width?: number | `${number}` | undefined; height?: number | `${number}` | undefined }) {
  return (milnetAssociation && (
    <div className={styles['military-badge']}>
      <NextImage src={agentAssociations[militaryBadgeMapping[milnetAssociation]]} alt={milnetAssociation} width={width} height={height} />
    </div>
  ));
}


function ListingAgentMobile({ agentInfo, showMilitaryDetails = false, onContactAgentClick, avatarWidth = DEFAULT_AGENT_AVATAR_WIDTH, avatarHeight = DEFAULT_AGENT_AVATAR_HEIGHT }: ListingAgentMobileProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const agentPic = agentInfo?.photo ? agentInfo?.photo : DEFAULT_AGENT_PIC;

  const handleContactAgentClick = () => {
    onContactAgentClick?.(agentInfo);
  };

  return (
    <div className={styles['component-mobile']}>
      <div className={styles['content-container']}>
        <div className={styles['top-section']}>
          <div className={styles['top-left-section']}>
            <NextImage src={agentPic} alt={agentInfo?.firstName} className={styles['avatar-large']} width={avatarWidth} height={avatarHeight}/>
          </div>
          <div className={styles['top-right-section']}>
            <p className={styles['agent-name']}>{agentInfo?.firstName} {agentInfo?.lastName}</p>
            <div className={styles['title-badge-container']}>
              <p className={styles['agent-location']}>{agentInfo?.title || t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_TITLE)}</p>
              {showMilitaryDetails && <MilitaryBadge milnetAssociation={agentInfo?.milnet?.milnetAssociation} />}
            </div>
            <p className={styles['agent-location']}>{agentInfo?.cityState}</p>
            {agentInfo?.email &&
            <div className={styles['copyable-text']}>
              <p>{agentInfo.email}</p>
              <CopyButton content={agentInfo.email} tooltipText={t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_TOOLTIP_EMAIL_COPIED)} />
            </div>
            }
            {agentInfo?.phoneNumber &&
            <div className={styles['copyable-text']}>
              <p>{agentInfo.phoneNumber}</p>
              <CopyButton content={agentInfo.phoneNumber} tooltipText={t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_TOOLTIP_PHONE_COPIED)} />
            </div>
            }
          </div>
        </div>
        <div className={styles['bottom-section']}>
          <Button className={styles['cta-button']} onClick={handleContactAgentClick} theme={SECONDARY_THEME} label={t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_CONTACT_AGENT)} />
        </div>
      </div>
    </div>
  );
}

function ListingAgentDesktop({ agentInfo, showMilitaryDetails = false, onContactAgentClick, avatarWidth = DEFAULT_AGENT_AVATAR_WIDTH, avatarHeight = DEFAULT_AGENT_AVATAR_HEIGHT }: ListingAgentMobileProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const agentPic = agentInfo?.photo ? agentInfo?.photo : DEFAULT_AGENT_PIC;

  const handleContactAgentClick = () => {
    onContactAgentClick?.(agentInfo);
  };

  return (
    <div className={styles.component}>
      <div className={styles['content-container']}>
        <div className={styles['top-section']}>
          <NextImage src={agentPic} alt={agentInfo?.firstName} className={styles['avatar-large']} width={avatarWidth} height={avatarHeight}/>
          <div className={styles['agent-info']}>
            <p className={styles['agent-name']}>{agentInfo?.firstName} {agentInfo?.lastName}</p>
          </div>
        </div>
        <div className={styles['bottom-section']}>
          <div className={styles['section-1']}>
            <div className={styles['title-badge-container']}>
              <p>{agentInfo?.title || t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_TITLE)}</p>
              {showMilitaryDetails && <MilitaryBadge milnetAssociation={agentInfo?.milnet?.milnetAssociation} />}
            </div>
            <p>{agentInfo.cityState}</p>
          </div>
          <div className={styles['section-2']}>
            {agentInfo?.email &&
                <div className={styles['copyable-text']}>
                  <p>{agentInfo.email}</p>
                  <CopyButton className={styles['copy-button']} content={agentInfo.email} tooltipText={t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_TOOLTIP_EMAIL_COPIED)} />
                </div>
            }
            {agentInfo?.phoneNumber &&
                <div className={styles['copyable-text']}>
                  <p>{agentInfo.phoneNumber}</p>
                  <CopyButton content={agentInfo.phoneNumber} tooltipText={t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_TOOLTIP_PHONE_COPIED)} />
                </div>
            }
          </div>
          <div className={styles['section-3']}>
            <Button className={styles['cta-button']} onClick={handleContactAgentClick} theme={SECONDARY_THEME} label={t(LOCALE_COMMON_PATHS.ADDRESS_LISTING_AGENT_CONTACT_AGENT)} />
          </div>
        </div>
      </div>
    </div>
  );
}

//#endregion

//#region Main Component
export default function ListingAgent({ expAgentId, showMilitaryDetails = false, onContactAgentClick }: ListingAgentProps) {
  // Directory fetch requires reCAPTCHA; load it here so this card does not depend on
  // MyListingAgentCard / MyLinkMyLead being present on the page.
  const { scriptLoaded, RecaptchaScript } = useRecaptcha();
  const isMobile = useSizeClass({ max: 980 });

  const { data: agentInfo = undefined } = useQuery<ListingAgentInfo>({
    queryKey: ['listing-agent', expAgentId],
    queryFn: async () => {
      const res = await fetchAgentDetailsByNumericKey(expAgentId);
      if (showMilitaryDetails && res?.milnet?.milnetAssociation){
        return {
          ...res,
          title: res?.milnet?.milnetAssociation,
        };
      }
      return res;
    },
    enabled: scriptLoaded,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const ListingAgentComponent = isMobile ? ListingAgentMobile : ListingAgentDesktop;

  return (
    <>
      {RecaptchaScript}
      {agentInfo && (
        <ListingAgentComponent
          agentInfo={agentInfo}
          showMilitaryDetails={showMilitaryDetails}
          onContactAgentClick={onContactAgentClick}
        />
      )}
    </>
  );
}
//#endregion